import {
  ApolloClient,
  ApolloClientOptions,
  from,
  InMemoryCache,
  InMemoryCacheConfig,
  NormalizedCacheObject,
} from "@apollo/client";
import { GetToken, SignOut } from "@clerk/types";
import merge from "deepmerge";
import { isEqual } from "lodash";
import { MoxieMobileApolloClient } from "../moxieMobile/MoxieMobileApolloClient";
import { MoxieMobileCache } from "../moxieMobile/MoxieMobileCache";
import { getCustomErrorLink } from "./links/customErrorLink";
import {
  getClientTransportLayerLink,
  getServerTransportLayerLink,
} from "./links/transportLayerLinks";

type PageProps<Props = Record<string, unknown>> = {
  props: Props;
};

export const APOLLO_STATE_PROPERTY_NAME = "__APOLLO_STATE__";
export const COOKIES_TOKEN_NAME = "auth";

let apolloClient: ApolloClient<NormalizedCacheObject> = null;

const cacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    medspa: {
      fields: {
        configuration: {
          // fixes error: configuration medspa either ensure all objects
          // of type MedspasMedspaconfiguration have an ID or a custom merge function
          merge: true,
        },
      },
    },
    // https://www.apollographql.com/docs/react/caching/cache-field-behavior#the-merge-function
    // By default, when refetching we would override the existing photos array with the new data.
    // We want to merge the arrays instead.
    client: {
      fields: {
        photos: {
          keyArgs: ["labelIn"],
          merge(
            existing: unknown[],
            incoming: unknown[],
            { variables: { offset = 0 } }
          ) {
            const mergedPhotos = existing ? [...existing] : [];
            if (mergedPhotos.length < offset) {
              for (let i = mergedPhotos.length; i < offset; i++) {
                mergedPhotos[i] = null;
              }
            }
            incoming.forEach((photo, idx) => {
              mergedPhotos[offset + idx] = photo;
            });

            return mergedPhotos;
          },
        },
      },
    },
  },
};

const createApolloClient = ({
  getClerkToken,
  signOut,
  isMobileApp,
}: {
  getClerkToken: GetToken;
  signOut?: SignOut;
  isMobileApp?: boolean;
}) => {
  const handleCustomErrorsLink = getCustomErrorLink();

  const defaultApolloConfig: Partial<ApolloClientOptions<unknown>> = {
    ssrMode: typeof window === "undefined",
    link: from([
      handleCustomErrorsLink,
      typeof window === "undefined"
        ? getServerTransportLayerLink({ getClerkToken, signOut })
        : getClientTransportLayerLink({ getClerkToken, signOut, isMobileApp }),
    ]),
  };

  return isMobileApp
    ? new MoxieMobileApolloClient({
        ...defaultApolloConfig,
        cache: new MoxieMobileCache({
          typePolicies: {
            ...cacheConfig.typePolicies,
            StripeTerminalType: {
              fields: {
                serialNumber: {
                  read(existing) {
                    if (typeof existing === "string") {
                      return existing.replace(/-moxieId-.*/, "");
                    }

                    return existing;
                  },
                },
              },
            },
          },
        }),
      })
    : new ApolloClient({
        ...defaultApolloConfig,
        cache: new InMemoryCache(cacheConfig),
      });
};

export function initializeApollo({
  initialState = null,
  getClerkToken,
  signOut,
  isMobileApp,
}: {
  initialState?: Record<string, unknown>;
  getClerkToken: GetToken;
  signOut?: SignOut;
  isMobileApp?: boolean;
}) {
  const client =
    apolloClient ?? createApolloClient({ getClerkToken, signOut, isMobileApp });

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    client.cache.restore(data as NormalizedCacheObject);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === "undefined") {
    return client;
  }

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = client;
  }

  return client;
}

export function addApolloState<T>(
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: PageProps<T>
) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROPERTY_NAME] = client.cache.extract();
  }

  return pageProps;
}
